import { Features } from '~/components/FeatureToggle';
import { version } from '../version';

interface ConfigType {
    [key: string]: string | number | boolean | Record<string, unknown>;
}

/**
 * Represents a document's URL and when the document in the URL was last updated.
 *
 * @property url - The URL as a string.
 * @property label - A human-readable label for the document, e.g. "License and Service Terms".
 * @property lastUpdated - Date indicating the last time the URL was updated in ISO 8601 format, e.g. "2025-09-23".
 */
export type TrackedDocumentUrl = {
    /** URL to document */
    url: string;
    /** A human-readable label for the document, e.g. "License and Service Terms" */
    label: string;
    /** Date indicating the last time the URL was updated in ISO 8601 format, e.g. "2025-09-23" */
    lastUpdated: string;
};

class AppConfig {
    private static config: ConfigType | null = null;
    private static configPromise: Promise<ConfigType> | null = null;

    private static async loadConfig(): Promise<ConfigType> {
        if (this.configPromise) {
            return this.configPromise;
        }

        this.configPromise = (async () => {
            const response = await fetch('/app-config.json');
            if (!response.ok) {
                throw new Error(`Failed to load config: ${response.status}`);
            }
            const config = await response.json();
            this.config = config;
            return config;
        })();

        return this.configPromise;
    }

    public static get backendUrl(): string {
        return AppConfig.get('backendUrl', 'https://dev.ai.api.mvision.ai');
    }

    public static get dashboardUrl(): string {
        return AppConfig.get('dashboardUrl', 'https://dashboard.mvision.ai');
    }

    public static get configUrl(): string {
        return AppConfig.get('configUrl', 'https://dev.config.mvision.ai');
    }

    /** Returns URL to a "license and service terms" document, or undefined if not configured.
     * This is mainly used in demo mode. Defaults to undefined to prevent linking to an obsolete
     * document by accident -- this field must be set in the deployment's app-config.json file
     * to display the document.
     */
    public static get licenseAndServiceTermsDocUrl():
        | TrackedDocumentUrl
        | undefined {
        return AppConfig.get('licenseAndServiceTermsDocUrl', undefined);
    }

    public static get authority(): string {
        return AppConfig.get('authority', '');
    }

    public static get clientId(): string {
        return AppConfig.get('clientId', '');
    }

    public static get accessRightKey(): string {
        return AppConfig.get('accessRightKey', '');
    }

    public static get pollingIntervalMs(): number {
        return parseInt(AppConfig.get('pollingIntervalMs', '1000'));
    }

    public static get sentryDsn(): string {
        return AppConfig.get('sentryDsn', '');
    }

    public static get sentryTracesSampleRate(): number {
        return AppConfig.get('sentryTracesSampleRate', 1.0);
    }

    public static get sentryReplaysSessionSampleRate(): number {
        return AppConfig.get('sentryReplaysSessionSampleRate', 0.1);
    }

    public static get sentryReplaysOnErrorSampleRate(): number {
        return AppConfig.get('sentryReplaysOnErrorSampleRate', 1.0);
    }

    public static get clinicalUse(): boolean {
        return AppConfig.get('clinicalUse', false);
    }

    /** True if this app should run in demo mode, false otherwise. Defaults to false. */
    public static get isDemo(): boolean {
        return AppConfig.get('isDemo', false);
    }

    public static get version(): string {
        return version;
    }

    public static isFeatureEnabled(
        feature: Features,
        defaultTo = false
    ): boolean {
        const features = AppConfig.get('features', {});
        return (features as Record<string, boolean>)[feature] ?? defaultTo;
    }

    public static async initialize(): Promise<void> {
        await this.loadConfig();
    }

    public static get<T>(key: string, defaultTo: T): T {
        if (!this.config) {
            console.warn(
                'AppConfig not initialized, using default value for',
                key
            );
            return defaultTo;
        }
        return ((this.config as ConfigType)[key] as T) || defaultTo;
    }
}

export default AppConfig;
